import { supabase } from './client';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

// Symptoms dictionary client. The schema mirrors `devices.ts` exactly;
// the admin UX is intentionally identical between the two so admins
// only have to learn one workflow. The user-side suggestion flow
// differs slightly: the tagging surfaces pair `suggestSymptom` with an
// immediate `setStrainSymptoms` call so the new pending row appears
// instantly in the user's list of selected symptoms.

export type SymptomStatus = 'pending' | 'approved';

export type Symptom = {
  id: string;
  name: string;
  status: SymptomStatus;
  created_by: string | null;
  created_at: string;
};

/**
 * List symptoms visible to the caller. RLS enforces the visibility
 * tier: anon/authenticated see approved + their own pending; admins
 * see everything.
 */
export async function fetchSymptoms(statusFilter?: SymptomStatus): Promise<Symptom[]> {
  let q = db
    .from('patient_symptoms')
    .select('id, name, status, created_by, created_at')
    .order('name');
  if (statusFilter) q = q.eq('status', statusFilter);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as Symptom[];
}

/**
 * User-side: suggest a new symptom. Returns the persisted row so
 * PortfolioRoute can immediately link it to the active portfolio
 * entry without a second round trip to find the new ID.
 *
 * If the symptom already exists (case-insensitive match) we return
 * the existing row instead of erroring on the unique-name index.
 * That's deliberate — most "new" submissions are actually rediscoveries
 * of an approved name typed slightly differently.
 */
export async function suggestSymptom(name: string): Promise<Symptom> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Sign in required');
  const trimmed = name.trim();
  if (!trimmed) throw new Error('Symptom name required');

  const existing = await db
    .from('patient_symptoms')
    .select('id, name, status, created_by, created_at')
    .ilike('name', trimmed)
    .maybeSingle();
  if (existing.data) return existing.data as Symptom;

  const { data, error } = await db
    .from('patient_symptoms')
    .insert({ name: trimmed, status: 'pending', created_by: user.id })
    .select('id, name, status, created_by, created_at')
    .single();
  if (error) throw error;
  return data as Symptom;
}

/** Admin: rename a symptom. */
export async function updateSymptomName(id: string, name: string): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('Symptom name required');
  const { error } = await db.from('patient_symptoms').update({ name: trimmed }).eq('id', id);
  if (error) throw error;
}

/** Admin: flip a pending symptom to approved. */
export async function approveSymptom(id: string): Promise<void> {
  const { error } = await db.from('patient_symptoms').update({ status: 'approved' }).eq('id', id);
  if (error) throw error;
}

/**
 * Admin: hard-delete a symptom. The `user_strain_symptoms.symptom_id` FK
 * cascades, so any user's tag pointing at this row silently disappears
 * from their strains. Prefer `mergeSymptom` for typo-fix scenarios so
 * users don't lose their tagging.
 */
export async function deleteSymptom(id: string): Promise<void> {
  const { error } = await db.from('patient_symptoms').delete().eq('id', id);
  if (error) throw error;
}

/**
 * Admin: merge a duplicate symptom into a canonical one. The
 * merge_symptom SECURITY DEFINER RPC reassigns user_strain_symptoms
 * links from old→new (skipping conflicts), then deletes the old row.
 */
export async function mergeSymptom(oldId: string, newId: string): Promise<void> {
  const { error } = await db.rpc('merge_symptom', { p_old: oldId, p_new: newId });
  if (error) throw error;
}
